// creature-thumbnail.ts — the creature picture/media/thumbnail family, carved out of the
// template.js god-file (XZ front-end TS closure, Phase A — 2026-06-13).
// See plans/xz-frontend-ts-closure.md.
//
// Picture selection and token dimensions share pure readers with ground-plane gameplay:
//   selectCreaturePicture(c, opts)   — picture-row priority walk (shapeshift → roll → EC galleries → PC → item)
//   selectCreatureMedia(c, opts)     — static picture selection for a map token
//   renderCreatureThumbnail(c, opts) — the full <div id="creature_…"> token HTML
//   getCreatureName(c)               — display name (PC name → EC name → 'N/A')
//
// HOME = the creature-render bundle on purpose: it loads AFTER template.js
// (template_page_footer.php ~585) so every call-time global these lean on
// (ftCreatureStatuses / pictureField0 / SIZE_TYPES / ftLayerValue / escapeHtml /
// ftEncounterCreatureField / window.buildSwarmGhostOverlayHTML / …) is already defined when
// these run. NOTHING here executes at module top level — same recipe as inventory/thumbnail.ts.
// All ambient deps are declared in ./globals.d.ts; the four exports are window-attached at the
// bottom so the existing bare-identifier callers (debug log, websocket encounter-update, the create-element / media-signature / animate-entity satellites
// in this bundle) keep resolving unchanged.

import { selectCreaturePicture as readCreaturePicture, creatureTokenGeometry } from '../shared/creature-token-geometry';
import type { CreaturePictureReaders } from '../shared/creature-token-geometry';

type CreatureBlob = Record<string, unknown>;

interface CreatureMedia {
    type: 'image';
    url: string;
    width: number;
    height: number;
}

interface CreatureThumbnailOptions {
    use_picture_thumbnail?: boolean;
    encounter_arr?: unknown;
}

type CreatureThumbnailWindow = Window & {
    ftIsEncounterCreatureLair?: (creature: unknown, encounterArr?: unknown) => boolean;
    ftGetLairTemplateName?: (creature: unknown) => string;
    iconMap?: typeof iconMap;
};

function recordOf(value: unknown): CreatureBlob | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CreatureBlob
        : null;
}

const pictureReaders: CreaturePictureReaders = {
    pictures: (row, key) => ftPicturesOf(row, key), itemPictures: row => ftItemPictures(row),
    items: (row, key) => ftItemsCol(row, key), statuses: row => ftCreatureStatuses(row, 'target'),
    paused: row => statusEffectInstanceIsCampaignPaused(row), picture: (row, key) => pictureField0(row, key),
};
export function selectCreaturePicture(value: unknown, options: CreatureThumbnailOptions = {}) {
    return readCreaturePicture(value, options, pictureReaders);
}

/**
 * Selects the best still image for a creature token.
 *
 * Video-backed token animations were the pre-FT3D presentation path. FT3D owns
 * creature animation now, so uploaded videos remain gallery media and never
 * replace a token's still picture.
 */
export function selectCreatureMedia(value: unknown, { use_picture_thumbnail = true }: { use_picture_thumbnail?: boolean } = {}): CreatureMedia | null {
    const c = recordOf(value);
    if (!c) return null;
    const pic = selectCreaturePicture(c, { use_picture_thumbnail });
    const picUrl = pictureField0(pic, 'pic_url');
    if (picUrl) {
        return {
            type: 'image',
            url: picUrl,
            width: Number(pictureField0(pic, 'picture_width')) || 960,
            height: Number(pictureField0(pic, 'picture_height')) || 960
        };
    }

    return null;
}

/**
 * Re-creates the HTML for ONE encounter-creature using the PHP-style
 * render_encounter_creature_summary() logic, but 100% client-side.
 *
 * @param creature                   parsedData.encounter_creatures
 * @param options
 * @param options.use_picture_thumbnail  If false → prefer full uncropped image (used by XZ Widget)
 * @returns full <div id="creature_…">…</div>
 */
export function renderCreatureThumbnail(creature: unknown, options: CreatureThumbnailOptions = {}): string {

	const {
        use_picture_thumbnail = true   // Default: use thumbnail when possible (good for combat widget, log, etc.)
    } = options;

    const c = recordOf(creature);
    if (!c) return '';
    // Use the LOCAL detector (item_type === 'lair' fallback), NOT the window global directly: the global
    // ftIsEncounterCreatureLair is never actually defined anywhere, so `typeof … === 'function'` was always
    // false → lairs fell through to the normal creature thumbnail (name-initials avatar). isEncounterCreatureLair
    // tries the global first, then falls back to item_type, so the lair is detected and gets its map token.
    if (isEncounterCreatureLair(c, options.encounter_arr)) {
        // Lairs are private, GM-only combat actors. Players never get a map token (return '');
        // the GM (and the encounter editor) gets a compact draggable generic-icon token so lair
        // AOE abilities can be anchored/originated on the grid. The creation-side gates
        // (encounter-update.ts) already skips non-GM lairs — this GM check
        // is the authoritative safety net so no render path can leak a lair token to a player.
        const viewerIsGM = (typeof window !== 'undefined' && window.FateTide?.user?.isGM === true)
            || ftIsModifyEncounterToolsPage();
        return viewerIsGM ? renderLairMapToken(c) : '';
    }
    const id = ftEncounterCreatureField(c.encounter_creatures, 'encounter_creature_id');

    const pic = selectCreaturePicture(c, { use_picture_thumbnail });
    const { sizeName, pixelH, heightFt, tokenW, widthFt } =
        creatureTokenGeometry(c, pic, SIZE_TYPES, ftLayerValue(c, 'size'), pictureField0);

    // --------------------------------------------------------------
    // 4. State flags
    // --------------------------------------------------------------
    const isDead = !!ftEncounterCreatureField(c.encounter_creatures, 'encounter_creature_dead');

    const viewerIsCampaignGm =
        typeof window !== 'undefined' && window.FateTide?.user?.isGM === true;
    const playerPermMOVE = ftHasPlayerPermission(c);
    const modifyEncounterTools = ftIsModifyEncounterToolsPage();

    // --------------------------------------------------------------
    // 5. CSS classes – NO visibility classes here anymore
    // --------------------------------------------------------------
    // Map dragging + remote move preview POSTs require player_permission on the token
    // (FT3D preview / ft3d_move_encounter_creature.php). GMs may drag anything.
    const classes = [
        'encounter-creature',
        (modifyEncounterTools || viewerIsCampaignGm || playerPermMOVE) ? 'draggable draggable-line' : '',
        'dropzone',
        'rounded',
        'border',
        isDead ? 'encounter-creature-dead' : ''
    ].filter(Boolean).join(' ');

    // --------------------------------------------------------------
    // 6. data-* attributes
    // --------------------------------------------------------------
    // Token data contract (2026-06-12, plans/xz-token-data-contract-and-widget-slimdown.md):
    // tokens carry IDENTITY + render geometry; gameplay scalars (team / merchant / reach /
    // weights / capacity / permission / dead flag) resolve from encounterArr via
    // js-src/encounter-map/token-data.ts. Dead state rides the encounter-creature-dead class.
    const data = {
        draggable_id: id,
        encounter_creature_id: id,
        item_type: ftItemsCol(c, 'item_type') ?? ftEncounterCreatureField(c.encounter_creatures, 'item_type') ?? '',
        dropzone_type: 'dropzone_give_inventory',
        dropzone_id: id,
        x: ftPosCoord(c, 'x_pos') ?? 0,
        z: ftPosCoord(c, 'z_pos') ?? 0,
        size: sizeName,
        // Stamped so the surgical encounter_update diff (websocket/encounter-update.ts) can detect a
        // number-only change (e.g. the renumber after an insert) and repaint the badge below without a full rebuild.
        creature_number: ftEncounterCreatureField(c.encounter_creatures, 'creature_number') ?? 0,
        thumbnail_width: tokenW,
        thumbnail_height: pixelH,
        width_ft: widthFt,
        height_ft: heightFt,
        preferred_speed_type: ftEncounterCreatureField(c.encounter_creatures, 'preferred_speed_type') ?? 'DF516479-F164-87FA-7BB8-6F268BF1DCE7',
        bright_light_dim_light: ftLayerValue(c, 'bright_light_dim_light') ?? 0,
        magical_light: ftLayerValue(c, 'magical_light') ?? 0,
        magical_darkness: ftLayerValue(c, 'magical_darkness') ?? 0,

        // Prev-frame animation slots — animate-entity.ts reads these off the OLD element
        // as the previous render's value to tween bars from. They are genuinely DOM state;
        // all other vitality/mana detail fields live in computed.vitality / encounter_creatures
        // and are read from the blob (stale data-* copies removed 2026-06-12).
        v_bar_v_percent: ftComputedValue(c, 'vitality', 'v_bar_v_percent') ?? 0,
        v_bar_v_temp_percent: ftComputedValue(c, 'vitality', 'v_bar_v_temp_percent') ?? 0,
        mana_percent: ftEncounterCreatureField(c.encounter_creatures, 'mana_percent') ?? 0,
    };

    const dataStr = Object.entries(data)
        .map(([k, v]) => `data-${k}="${escapeHtml(String(v))}"`)
        .join(' ');

    // --------------------------------------------------------------
    // 7. Name + Initials
    // --------------------------------------------------------------
    const name = getCreatureName(c);
    const initials = name
        ? name.charAt(0).toUpperCase() + (name.length > 1 ? name.charAt(1).toLowerCase() : '')
        : '??';

	// --------------------------------------------------------------
	// 8. Media: Static image → Initials
	// --------------------------------------------------------------
	let thumbContent = '';

	const media = selectCreatureMedia(c, { use_picture_thumbnail });

	if (media) {
		// Fill the token box at any zoom; avoid fixed max-* caps
		thumbContent = `
			<img src="${media.url}" class="d-block ft3d-token-media w-100"
				 style="width:100%;height:100%;max-width:none;max-height:none;object-fit:contain;pointer-events:none;border-radius:0.375rem;"
				 alt="${escapeHtml(name)}">
		`;
	} else {
		// Initials fallback — outer token sets size at zoom; fill box
		thumbContent = `
			<div class="bg-warning border border-dark rounded d-flex justify-content-center align-items-center w-100 h-100">
				<span class="fw-bold h4 text-dark">${initials}</span>
			</div>
		`;
	}

    // --------------------------------------------------------------
    // 9. Creature number badge
    // --------------------------------------------------------------
    const creatureNum = ftEncounterCreatureField(c.encounter_creatures, 'creature_number') ?? 0;
    const numBadge = (creatureNum > 0)
        ? `<div class="position-absolute bottom-0 start-0">
               <span class="badge bg-dark text-light border">${creatureNum}</span>
           </div>`
        : '';


    // --------------------------------------------------------------
    // 11. Action points
    // --------------------------------------------------------------
    const ap = ftEncounterCreatureField(c.encounter_creatures, 'aps') ?? '';
    const reactionIcon = ftEncounterCreatureField(c.encounter_creatures, 'reaction') == 1
        ? 'ap_reaction_dark_mode.png'
        : 'ap_reaction_used_dark_mode.png';
    const apHtml = ap ? `<span class="badge bg-dark border">${ap}</span>` : '';
    const apBlock = `
        <div class="ap-dots" style="pointer-events:auto;">
            ${apHtml}<img src="https://cdn.fatetide.com/images/icons/${reactionIcon}" width="15" height="15" style="pointer-events:none;">
        </div>`;

    // --------------------------------------------------------------
    // 12. HP bar with label
    // --------------------------------------------------------------
    const vCurrent = ftComputedValue(c, 'vitality', 'v_current') ?? 0;
    const vMax     = ftComputedValue(c, 'vitality', 'v_max') ?? 0;
    const vTemp    = ftComputedValue(c, 'vitality', 'v_temp') ?? 0;
    const vMaxTemp = ftComputedValue(c, 'vitality', 'v_max_temp') ?? 0;

    const vBarTotal = ftComputedValue(c, 'vitality', 'v_bar_total') ?? 0;
    const vBarPct   = ftComputedValue(c, 'vitality', 'v_bar_v_percent') ?? 0;
    const tempBarPct = ftComputedValue(c, 'vitality', 'v_bar_v_temp_percent') ?? 0;

    let hpLabel = `${vCurrent}/${vMax}`;
    if (vMaxTemp !== 0) hpLabel += ` (${vMaxTemp})`;
    if (vTemp > 0) hpLabel += ` +${vTemp}`;

    // Always emit `.hp-temp`; `animateEntityChanges` needs this node when temp V arrives after first paint (`buildCombatWidget` rebuilds and does not have this gap).
    let hpFill = `<div class="progress-bar hp-main" style="width:${vBarPct}%"></div>`;
    hpFill += `<div class="progress-bar hp-temp" style="width:${tempBarPct}%;display:${tempBarPct > 0 ? 'block' : 'none'};"></div>`;

    const hpBlock = `
        <div class="hp-bar" data-hp-label="${hpLabel}">
            <div class="progress">
                ${hpFill}
            </div>
        </div>`;

    // --------------------------------------------------------------
    // 13. Mana bar
    // --------------------------------------------------------------
    const hasMana = !!ftEncounterCreatureField(c.encounter_creatures, 'spirit') && ftEncounterCreatureField(c.encounter_creatures, 'mana_percent') !== undefined;
    const manaBlock = hasMana
        ? `<div class="mana-bar" style="pointer-events:auto;">
                <div class="progress border">
                    <div class="progress-bar" style="width:${ftEncounterCreatureField(c.encounter_creatures, 'mana_percent')}%"></div>
                </div>
           </div>`
        : '';

    // --------------------------------------------------------------
    // 15. Static overlay (HP, AP, mana) – only for alive creatures
    // --------------------------------------------------------------
    const overlayCls = ['encounter-creature-overlay', 'position-absolute', 'top-0', 'start-0', 'w-100', 'h-100', 'pointer-events-none'];
    if (hasMana) overlayCls.push('has-mana');

    const overlay = !isDead ? `
        <div class="${overlayCls.join(' ')}" style="z-index:100;">
            ${apBlock}${hpBlock}${manaBlock}
        </div>` : '';

    // --------------------------------------------------------------
    // 15b. Machinist orb arc — the StS-style floating orbs above the token.
    // encounter_creatures.orb_queue is the compiled DECODED slug list (index 0 = oldest =
    // next to evoke). Display order mirrors Dualcast's authored prose: the NEXT-TO-EVOKE
    // orb sits RIGHTMOST, new orbs fill in from the left; a shallow arc via per-index
    // vertical offsets. Pure read — the queue is engine state, this only draws it.
    // --------------------------------------------------------------
    const orbArcBlock = buildOrbArcHTML(c, tokenW, isDead);

    // --------------------------------------------------------------
    // 16. Swarm ghost overlay (creature-render-bundle, no-op for non-swarms)
    // --------------------------------------------------------------
    const swarmPicUrl = pictureField0(pic, 'pic_url') || '';
    const swarmGhostOverlay = (typeof window.buildSwarmGhostOverlayHTML === 'function')
        ? window.buildSwarmGhostOverlayHTML(c, swarmPicUrl)
        : '';

    // --------------------------------------------------------------
    // 17. Final HTML
    // --------------------------------------------------------------
    const style = `position:absolute;width:${tokenW}px;height:${pixelH}px;`;

    return `
        <div
			id="creature_${id}"
            class="${classes}"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="${escapeHtml(name)}"
            data-bs-title="${escapeHtml(name)}"
            ${dataStr}
            style="${style}"
		>
            <div class="position-relative d-block w-100 h-100" style="--tw:${tokenW}px;--th:${pixelH}px;">
                ${thumbContent}
                ${numBadge}
                ${overlay}
                ${orbArcBlock}
                ${swarmGhostOverlay}
            </div>
        </div>`;
}

// Machinist orb visuals: per-type glow colors (StS palette adjacent). Slugs are the OrbType
// catalog's variable_names; unknown slugs are skipped defensively.
const ORB_ARC_COLORS: Record<string, string> = {
    lightning: '#f5d90a',
    cold:      '#6ec6ff',
    necrotic:  '#8b2fd6',
    thunder:   '#4757d6',
    fire:      '#ff7a1a',
    force:     '#c08cff',
    poison:    '#35c235',
    acid:      '#9acd32',
};

/** The floating orb arc above a Machinist token; '' when the creature holds no orbs. */
export function buildOrbArcHTML(creature: unknown, tokenW: number, isDead: boolean): string {
    if (isDead) return '';
    const c = recordOf(creature);
    const rawQueue = recordOf(c?.encounter_creatures)?.orb_queue;
    // hasOwnProperty.call, NOT `in`: `in` accepts inherited keys ('constructor', '__proto__'), which
    // breaks the whitelist boundary under prototype pollution (Codex slice-G review). The
    // slice(0, 10) is a render bound — the engine caps the queue at slot max anyway.
    // ⚠ NOT Object.hasOwn, which is ES2022 while this project's lib is ES2020 — it failed typecheck
    // (TS2550) rather than only at runtime. Same own-property semantics, and it is what
    // orbQueueOf() in the PC sheet and four other call sites already use.
    const queue = (Array.isArray(rawQueue)
        ? rawQueue.filter((s): s is string => typeof s === 'string' && Object.prototype.hasOwnProperty.call(ORB_ARC_COLORS, s))
        : []).slice(0, 10);
    if (queue.length === 0) return '';

    // Index 0 (next to evoke) renders RIGHTMOST → display order is the queue reversed.
    const display = [...queue].reverse();
    const n = display.length;
    const orbPx = Math.max(9, Math.min(15, Math.round(tokenW / 5)));
    const orbs = display.map((slug, i) => {
        const queueIndex = n - 1 - i;
        const isNext = queueIndex === 0;
        // Shallow arc: ends dip, middle rises (parabola over display position).
        const t = n === 1 ? 0.5 : i / (n - 1);
        const lift = Math.round(Math.sin(t * Math.PI) * orbPx * 0.6);
        const color = ORB_ARC_COLORS[slug];
        const label = `${slug.charAt(0).toUpperCase()}${slug.slice(1)} Orb${isNext ? ' — next to evoke' : ''}`;
        return `<span
            class="ft3d-orb${isNext ? ' ft3d-orb-next' : ''}"
            title="${escapeHtml(label)}"
            style="display:inline-block;width:${orbPx}px;height:${orbPx}px;border-radius:50%;
                margin:0 1px;transform:translateY(-${lift}px);
                background:radial-gradient(circle at 35% 30%, #ffffffcc 0%, ${color} 45%, ${color}33 100%);
                box-shadow:0 0 ${isNext ? 7 : 4}px 1px ${color}${isNext ? 'ee' : '88'};
                ${isNext ? `outline:1px solid ${color};outline-offset:1px;` : ''}"
        ></span>`;
    }).join('');

    return `
        <div class="ft3d-orb-arc position-absolute w-100 text-center pointer-events-none"
            style="top:-${orbPx + 8}px;left:0;z-index:101;line-height:1;direction:ltr;">
            ${orbs}
        </div>`;
}

export function getCreatureName(creature: unknown): string {
    const c = recordOf(creature);
    if (!c) return 'N/A';
    return String(recordOf(c.player_characters)?.player_name
        ?? ftEncounterCreatureField(c.encounter_creatures, 'encounter_creature_name')
        ?? 'N/A');
}

/** True when the encounter row is a lair actor (item_type = 'lair'). */
export function isEncounterCreatureLair(creature: unknown, encounterArr?: unknown): boolean {
    const host = window as CreatureThumbnailWindow;
    if (typeof host.ftIsEncounterCreatureLair === 'function') {
        return host.ftIsEncounterCreatureLair(creature, encounterArr);
    }
    const c = recordOf(creature);
    if (!c) return false;
    const fromEc = ftEncounterCreatureField(c.encounter_creatures, 'item_type');
    if (fromEc === 'lair') return true;
    return ftItemsCol(c, 'item_type') === 'lair';
}

/**
 * GM-only compact lair map token. Lairs have no picture/size/vitality, so this renders a fixed
 * Medium-box generic lair icon that carries the SAME token data contract as a normal creature
 * token (id, .encounter-creature, data-x/z, data-encounter_creature_id, data-item_type='lair',
 * draggable for the GM). That contract is what lets positionAllElements place it, the drag
 * controller move it, and the AOE resolver anchor a lair ability's template on it.
 */
function renderLairMapToken(c: CreatureBlob): string {
    const id = ftEncounterCreatureField(c.encounter_creatures, 'encounter_creature_id');
    const name = getLairTemplateName(c);

    // Lairs carry no `size` layer → render at the Medium token box. Resolve by semantic tier,
    // not a copied UUID: the former UUID was Tiny, making every lair a 40px / 2.5ft speck.
    const sizeInfo = SIZE_TYPES.find((s) => s.variable_name === 'medium')
        ?? { item_id: '', variable_name: 'medium', weight_multiplier: 10, base_size: 5, thumbnail_width: 80 };
    const box = sizeInfo.thumbnail_width;
    const sizeName = sizeInfo.variable_name;

    const viewerIsGM = (typeof window !== 'undefined' && window.FateTide?.user?.isGM === true);
    const draggable = (viewerIsGM || ftIsModifyEncounterToolsPage()) ? 'draggable draggable-line' : '';

    // Prefer the lair's OWN picture when one is set (media manager gallery); otherwise fall back to the
    // generic Lair icon — never the name-initials avatar a normal creature would get.
    const lairPic = selectCreaturePicture(c, { use_picture_thumbnail: false });
    const lairPicUrl = pictureField0(lairPic, 'pic_url');
    const iconMapFn = (window as CreatureThumbnailWindow).iconMap;
    const iconHtml = lairPicUrl
        ? `<img src="${escapeHtml(String(lairPicUrl))}" class="d-block w-100" style="width:100%;height:100%;object-fit:contain;pointer-events:none;border-radius:0.375rem;" alt="${escapeHtml(name)}">`
        : ((typeof iconMapFn === 'function')
            ? iconMapFn('lair', box, box, false)
            : `<div class="bg-secondary border border-dark rounded d-flex justify-content-center align-items-center w-100 h-100"><span class="fw-bold h4 text-light">L</span></div>`);

    const data = {
        draggable_id: id,
        encounter_creature_id: id,
        item_type: 'lair',
        dropzone_type: 'dropzone_give_inventory',
        dropzone_id: id,
        x: ftPosCoord(c, 'x_pos') ?? 0,
        z: ftPosCoord(c, 'z_pos') ?? 0,
        size: sizeName,
        is_lair: 1,
        creature_number: 0,
        thumbnail_width: box,
        thumbnail_height: box,
        width_ft: sizeInfo.base_size,
        height_ft: sizeInfo.base_size,
    };
    const dataStr = Object.entries(data)
        .map(([k, v]) => `data-${k}="${escapeHtml(String(v))}"`)
        .join(' ');

    const classes = ['encounter-creature', 'ft-lair-map-token', draggable, 'dropzone', 'rounded', 'border']
        .filter(Boolean).join(' ');
    const style = `position:absolute;width:${box}px;height:${box}px;`;

    return `
        <div
            id="creature_${id}"
            class="${classes}"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="${escapeHtml(name)}"
            data-bs-title="${escapeHtml(name)}"
            ${dataStr}
            style="${style}"
        >
            <div class="position-relative d-block w-100 h-100" style="--tw:${box}px;--th:${box}px;">
                ${iconHtml}
            </div>
        </div>`;
}

/** Catalog / row name for lair hover text (combat widget, roster). */
export function getLairTemplateName(creature: unknown): string {
    const host = window as CreatureThumbnailWindow;
    if (typeof host.ftGetLairTemplateName === 'function') {
        return host.ftGetLairTemplateName(creature);
    }
    const c = recordOf(creature);
    if (!c) return 'N/A';
    const fromTemplate = ftItemsCol(c, 'item_name');
    if (fromTemplate && String(fromTemplate).trim() !== '') {
        return String(fromTemplate).trim();
    }
    const fromRow = ftEncounterCreatureField(c.encounter_creatures, 'encounter_creature_name');
    if (fromRow && String(fromRow).trim() !== '') {
        return String(fromRow).trim();
    }
    return getCreatureName(c);
}

// -------------------------------------------------------------------------------------------
// Window attach — keep the historical bare-identifier global surface so every existing caller
// (debug log, websocket encounter-update.ts, and the create-element /
// media-signature / animate-entity satellites in THIS bundle) resolves unchanged. The four are
// ambient-declared in ./globals.d.ts; within this module they are real, typed, refactor-safe.
// -------------------------------------------------------------------------------------------
Object.assign(window, {
    selectCreaturePicture,
    selectCreatureMedia,
    renderCreatureThumbnail,
    getCreatureName,
    isEncounterCreatureLair,
    getLairTemplateName,
});
